<template>
    <div class="container-fluid">
        <div v-if="showLoader" class="p-3">
            <loader />
        </div>
        <template v-else>
            <div class="d-flex justify-content-between">
                <breadcrumb class="bg-light pb-0" :items="breadcrumbs" />
                <!-- Treatment Summary Button Section -->
                <span
                    v-if="
                        treatment.start === 0 &&
                        _hasAccess('schedule.treatmentboard.start')
                    "
                    class="d-flex"
                >
                    <button
                        class="btn btn-secondary treatment-action-button"
                        type="button"
                        @click="start"
                    >
                        Start
                    </button>
                    <button
                        type="button"
                        class="btn btn-secondary treatment-action-button pl-2"
                        @click="removeTreatment"
                        :href="'javascript:void(0)'"
                    >
                        Remove
                    </button>
                </span>
                <template
                    v-if="
                        _hasAccess('schedule.treatmentboard.end') &&
                        treatment.start > 0 &&
                        treatment.treatment_status[0].status_id >
                            TREATMENT_STATUS.ADMITTED
                    "
                >
                    <button
                        type="button"
                        class="btn btn-secondary treatment-action-button"
                        @click="end"
                    >
                        End
                    </button>
                </template>
            </div>
            <treatmentPatientDetails
                :treatment="treatment"
                :triageOptions="triageOptions"
                :dialogs="dialogs"
                class="pb-3"
            ></treatmentPatientDetails>
            <div ref="bottomSection" class="row pb-3">
                <div class="col text-right" md="6">
                    <div class="d-flex align-items-center justify-content-end">
                        <a
                            v-if="
                                activitiesOverdue.length > 0 &&
                                treatment.treatment_status[0].status_id !==
                                    TREATMENT_STATUS.ADMITTED
                            "
                            :href="'javascript:void(0)'"
                            id="overdue_summary"
                            class="pr-3"
                        >
                            {{ activitiesOverdue.length }}&nbsp;Overdue&nbsp;
                        </a>
                        <div
                            class="btn-group"
                            role="group"
                            aria-label="Add new or jump to"
                        >
                            <TreatmentActionsDropdown
                                v-if="
                                    status.id == TREATMENT.PRETREATMENT ||
                                    status.id == TREATMENT.TREATMENT
                                "
                                :patient="treatment.patient"
                                :disabled="treatment.start == 0"
                            >
                            </TreatmentActionsDropdown>
                        </div>
                        <div
                            class="btn-group mx-1 button-arrow"
                            role="group"
                            aria-label="Scroll right or left as well as a calendar"
                        >
                            <button
                                type="button"
                                class="btn btn-secondary"
                                v-b-tooltip.hover.top
                                title="Navigate timeline to the left"
                                @click="
                                    $refs.treatmentPatientActivities.onNavigateButtonClick(
                                        false
                                    )
                                "
                            >
                                <span
                                    class="fc-icon fc-icon-chevron-left font-weight-bolder"
                                ></span>
                            </button>
                            <button
                                type="button"
                                id="treatment-calendar-button"
                                v-b-tooltip.hover.top
                                title="Select date from calendar"
                                class="btn btn-secondary"
                            >
                                <span class="far fa-calendar"></span>
                            </button>
                            <button
                                type="button"
                                class="btn btn-secondary"
                                v-b-tooltip.hover.top
                                title="Navigate timeline to the right"
                                @click="
                                    $refs.treatmentPatientActivities.onNavigateButtonClick()
                                "
                            >
                                <span
                                    class="fc-icon fc-icon-chevron-right font-weight-bolder"
                                ></span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <treatment-patient-activities
                ref="treatmentPatientActivities"
                :treatment="treatment"
                :activities="segregateActivities(treatment.activities)"
                :dialogs="dialogs"
                :labelOptions="labelOptions"
                :routeOptions="routeOptions"
                :locationOptions="locationOptions"
            ></treatment-patient-activities>
            <treatment-calendar
                ref="treatmentCalendar"
                :target="'treatment-calendar-button'"
                @refresh="
                    date =>
                        $refs.treatmentPatientActivities.setToSelectedDate(date)
                "
                @set-to-current-time="
                    $refs.treatmentPatientActivities.setToCurrentTime()
                "
            />
            <activity-list-group
                v-if="
                    activitiesOverdue.length > 0 &&
                    treatment.treatment_status[0].status_id !==
                        TREATMENT_STATUS.ADMITTED
                "
                :treatment="treatment"
                target="overdue_summary"
                :activities="activitiesOverdue"
                :dialogs="dialogs"
                :placement="'rightBottom'"
                ref="activityListPopover"
                :parentRef="$refs.bottomSection"
                :labelOptions="labelOptions"
            >
            </activity-list-group>
        </template>
    </div>
</template>

<script>
import ScheduleHelper from "../helper";
import { find, some, isEmpty } from "lodash";
import TriageService from "@/services/triage";
import Loader from "@/components/elements/loader-card";
import TreatmentCalendar from "./treatment-calendar-popover";
import Breadcrumb from "@/components/commons/breadcrumb.vue";
import ActivityListGroup from "./treatment-activity-list-popover";
import TreatmentActionsDropdown from "./treatment-actions-dropdown";
import TreatmentPatientDetails from "./treatment-patient-details";
import TreatmentPatientActivities from "./treatment-patient-activities";
import TreatmentTimelineMixin from "@/mixins/treatment-timeline-details";

export default {
    name: "treatmentPatientView",
    mixins: [ScheduleHelper, TreatmentTimelineMixin],
    components: {
        Loader,
        Breadcrumb,
        ActivityListGroup,
        TreatmentCalendar,
        TreatmentPatientDetails,
        TreatmentActionsDropdown,
        TreatmentPatientActivities
    },
    data() {
        return {
            breadcrumbs: [],
            triageOptions: [],
            activitiesOverdue: []
        };
    },
    props: {
        treatment: {
            type: Object,
            default() {
                return {};
            }
        },
        loading: {
            type: Boolean,
            default: true
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    mounted() {
        this.getTriageList();
    },
    computed: {
        showLoader() {
            return this.loading && isEmpty(this.treatment);
        },
        /**
         * Translate index and module name to tbl_status id
         * @return {Object} status object.
         */
        status() {
            return some(this.$store.state.status)
                ? find(this.$store.state.status, {
                      index: this.treatment.treatment_status[0].status_id,
                      module_name: "schedule.treatment"
                  })
                : {};
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        },
        routeOptions() {
            return this.$store.state.reference["master-vaccination-route"];
        },
        locationOptions() {
            return this.$store.state.reference["master-vaccination-location"];
        }
    },
    watch: {
        treatment: {
            handler(val) {
                if (isEmpty(val)) return;

                this.breadcrumbs = [
                    {
                        text: "Treatment Board",
                        to: {
                            name: "treatment"
                        }
                    },
                    {
                        text: this.setPatientName(this.treatment.patient)
                    }
                ];
                let allActivities = this.prepareActivities(val, true);
                this.activitiesOverdue = allActivities.overdue;
            },
            immediate: true
        }
    },
    methods: {
        overdue_dialog(activity) {
            this.$refs.activityListPopover.openModal(activity);
        },
        getTreatment(id) {
            return this.$http
                .get(process.env.BARRACUDA + "schedule/treatment-board/" + id)
                .then(response => {
                    if (response.status == 200) {
                        this.treatment = response.data.response.resources;
                    } else {
                        this._message(
                            response.message ||
                                response.statusText ||
                                "Failed to fetch patient's treatment information",
                            "danger"
                        );
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        setPatientName(patient) {
            if (!patient) return;

            let patientName = patient.name;
            let patientOwner = null;
            if (patient.primary_owner && patient.primary_owner.length > 0) {
                patientOwner = `${patientName} ${patient.primary_owner[0].lastname}`;
                return patientOwner;
            }
            return patientName;
        },
        async getTriageList() {
            await TriageService.getTriageTypeLists()
                .then(res => {
                    if (res && res.code === 200) {
                        this.triageOptions = res.resources;
                    } else {
                        throw new Error(
                            "Error: Unable to get the triage list."
                        );
                    }
                })
                .catch(e => {
                    this._message(
                        "Error: Unable to get the triage list.",
                        "danger"
                    );
                });
        },
        segregateActivities(activities) {
            // Use reduce to group items by category
            return activities.reduce(
                (acc, item) => {
                    // Check if the item is active
                    if (!item.isactive) {
                        return acc; // Skip inactive items
                    }

                    // Get the category using the treatment activity type ID
                    const category = this.TREATMENT_TYPE[
                        item.treatment_activity_type_id
                    ];

                    // Initialize the array for the category if it doesn’t exist
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    acc[category].push(item);

                    return acc;
                },
                { Medical: [], Vitals: [], Other: [] } // Default structure
            );
        },
        start() {
            this.dialogs["startModal"].open({
                patient: this.treatment.patient,
                clientID: this.treatment.client_id,
                treatmentID: this.treatment.id,
                treatment: this.treatment,
                weight: find(this.vitals, { type: 4 })
            });
        },
        removeTreatment() {
            this.dialogs["removeTreatmentModal"].open({
                ...this.treatment
            });
        },
        end() {
            this.dialogs["dischargeModal"].open({
                ...this.treatment,
                status: some(
                    this.treatment.activities.filter(
                        activity =>
                            activity.status ===
                            this.TREATMENT_ACTIVITY_STATUS.PENDING
                    )
                )
                    ? this.TREATMENT_STATUS.CANCELLED
                    : this.TREATMENT_STATUS.DISCHARGED
            });
        }
    }
};
</script>
<style scoped>
.treatment-action-button {
    margin-top: 6px;
    margin-right: 5px;
    margin-bottom: 8px;
}
.vertical-line {
    width: 1px;
    height: 20px;
    background-color: lightgray;
    margin-top: 12px;
}
</style>
